/**
 * 현재 실행 환경을 감지합니다.
 * @returns {string} 환경 이름 (development, staging, prod)
 */
function detectEnvironment() {
  // 1순위: 환경변수로 명시적 설정
  const explicitEnv = import.meta.env.VITE_APP_ENV;
  if (explicitEnv) {
    return explicitEnv;
  }

  // 2순위: URL 파라미터로 환경 지정 (새로고침시에도 유지)
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const envParam = urlParams.get("env");
    if (envParam && ["staging", "prod"].includes(envParam)) {
      // localStorage에 저장하여 새로고침시에도 유지
      localStorage.setItem("app_environment", envParam);
      return envParam;
    }

    // 3순위: localStorage에서 이전 설정 읽기
    const savedEnv = localStorage.getItem("app_environment");
    if (savedEnv && ["staging", "prod"].includes(savedEnv)) {
      return savedEnv;
    }
  }

  // 4순위: 빌드 모드 기반
  if (import.meta.env.MODE === "production") {
    return "prod";
  } else if (import.meta.env.MODE === "staging") {
    return "staging";
  } else {
    return "development";
  }
}

/**
 * 환경별 기본 설정을 정의합니다.
 */
const defaultConfigs = {
  development: {
    API_BASE_URL: "http://localhost:8080",
    API_TIMEOUT: 120000,
    ENABLE_QUERY_DEVTOOLS: true,
  },
  staging: {
    API_BASE_URL: "",
    API_TIMEOUT: 120000,
    ENABLE_QUERY_DEVTOOLS: false,
  },
  prod: {
    API_BASE_URL: "",
    API_TIMEOUT: 120000,
    ENABLE_QUERY_DEVTOOLS: false,
  },
};

/**
 * 환경을 수동으로 설정합니다.
 * @param {string} env 환경 이름 (staging, prod1, prod2)
 */
export function setEnvironment(env) {
  if (typeof window !== "undefined" && ["staging", "prod"].includes(env)) {
    localStorage.setItem("app_environment", env);
    // 페이지 새로고침으로 즉시 적용
    window.location.reload();
  }
}

/**
 * 현재 환경을 반환합니다.
 * @returns {string} 현재 환경 이름
 */
export function getCurrentEnvironment() {
  return detectEnvironment();
}

/**
 * 환경변수와 런타임 감지를 통해 설정을 로드합니다.
 * @returns {Object} 현재 환경에 맞는 설정 객체
 */
export function loadConfig() {
  const currentEnv = detectEnvironment();
  const defaultConfig = defaultConfigs[currentEnv] || defaultConfigs.staging;

  // 환경변수가 있으면 기본값 오버라이드
  const config = {
    API_BASE_URL:
      import.meta.env.VITE_API_BASE_URL || defaultConfig.API_BASE_URL,
    API_TIMEOUT:
      parseInt(import.meta.env.VITE_API_TIMEOUT) || defaultConfig.API_TIMEOUT,
    ENABLE_QUERY_DEVTOOLS:
      import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS !== undefined
        ? import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === "true"
        : defaultConfig.ENABLE_QUERY_DEVTOOLS,
    ENVIRONMENT: currentEnv,
  };

  return config;
}

/**
 * 특정 설정 값을 가져옵니다.
 * @param {string} key 설정 키
 * @param {*} defaultValue 기본값
 * @returns {*} 설정 값
 */
export function getConfigValue(key, defaultValue = null) {
  const config = loadConfig();
  return config[key] !== undefined ? config[key] : defaultValue;
}
